// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/AskV2RightRail.test.tsx
import {
  CheckCircle,
  Circle,
  CircleNotch,
  FileText,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

import { AskV2RailSection } from "./AskV2RailSection";
import { hasRailContent } from "./collectRailContent";
import { type AskV2RailContent, type AskV2RailSectionId } from "./types";

type AskV2RightRailProps = {
  content: AskV2RailContent;
  sectionOpen: Record<AskV2RailSectionId, boolean>;
  onSectionOpenChange: (id: AskV2RailSectionId, open: boolean) => void;
};

function ProgressIcon({ status }: { status: "pending" | "active" | "done" }) {
  if (status === "done") {
    return (
      <CheckCircle
        size={16}
        weight="fill"
        className="shrink-0 text-puntt-accent-9"
        aria-hidden="true"
      />
    );
  }
  if (status === "active") {
    return (
      <CircleNotch
        size={16}
        className="shrink-0 animate-spin text-puntt-neutral-gray-9"
        aria-hidden="true"
      />
    );
  }
  return (
    <Circle
      size={16}
      className="shrink-0 text-puntt-neutral-gray-8"
      aria-hidden="true"
    />
  );
}

export function AskV2RightRail({
  content,
  sectionOpen,
  onSectionOpenChange,
}: AskV2RightRailProps) {
  const { t } = useTranslation("ui");

  if (!hasRailContent(content)) return null;

  return (
    <aside
      className="ask-puntt-surface flex h-full w-[320px] shrink-0 flex-col gap-3 overflow-y-auto overscroll-contain p-3 [scrollbar-width:thin]"
      data-testid="ask-v2-right-rail"
    >
      {content.progress ? (
        <AskV2RailSection
          id="progress"
          title={t("askV2.rail.progress")}
          open={sectionOpen.progress}
          onOpenChange={(open) => onSectionOpenChange("progress", open)}
        >
          {content.progress.status ? (
            <p
              className="mb-3 text-[11px] font-medium text-puntt-neutral-gray-9"
              data-testid="ask-v2-rail-progress-status"
            >
              {content.progress.status}
            </p>
          ) : null}
          <ul
            className="flex flex-col gap-2"
            data-testid="ask-v2-rail-progress-list"
          >
            {content.progress.items.map((item) => (
              <li
                key={item.id}
                className="flex items-start gap-2 text-[13px] text-puntt-neutral-gray-12"
                data-testid={`ask-v2-rail-progress-item-${item.id}`}
              >
                <ProgressIcon status={item.status} />
                <span className="min-w-0">
                  <span className="block leading-snug">{item.label}</span>
                  {item.detail ? (
                    <span className="block text-[12px] text-puntt-neutral-gray-10">
                      {item.detail}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </AskV2RailSection>
      ) : null}

      {content.output ? (
        <AskV2RailSection
          id="output"
          title={t("output")}
          open={sectionOpen.output}
          onOpenChange={(open) => onSectionOpenChange("output", open)}
        >
          <ul
            className="flex flex-col gap-2"
            data-testid="ask-v2-rail-output-list"
          >
            {content.output.items.map((item) => (
              <li
                key={item.id}
                className="flex items-center gap-3 rounded-lg border border-puntt-neutral-gray-5 bg-base-white px-3 py-2"
                data-testid={`ask-v2-rail-output-${item.kind}-${item.id}`}
              >
                {item.thumbnailUrl ? (
                  <img
                    src={item.thumbnailUrl}
                    alt=""
                    className="size-10 shrink-0 rounded-md object-cover"
                  />
                ) : (
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-puntt-neutral-gray-3 text-puntt-neutral-gray-10">
                    <FileText size={16} aria-hidden="true" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium text-puntt-neutral-gray-12">
                    {item.title}
                    {item.versionNumber != null ? (
                      <span className="ml-1 text-[11px] font-normal text-puntt-neutral-gray-10">
                        v{item.versionNumber}
                      </span>
                    ) : null}
                  </p>
                  {item.aiCommentCount != null ||
                  item.humanCommentCount != null ? (
                    <p className="text-[11px] text-puntt-neutral-gray-10">
                      {[
                        item.aiCommentCount != null
                          ? `${item.aiCommentCount} AI`
                          : null,
                        item.humanCommentCount != null
                          ? `${item.humanCommentCount} human`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </AskV2RailSection>
      ) : null}

      {content.context ? (
        <AskV2RailSection
          id="context"
          title={t("context")}
          open={sectionOpen.context}
          onOpenChange={(open) => onSectionOpenChange("context", open)}
        >
          <div
            className="flex flex-col gap-4"
            data-testid="ask-v2-rail-context-body"
          >
            {content.context.overview ? (
              <div data-testid="ask-v2-rail-overview">
                <h3 className="mb-1 text-[13px] font-bold text-puntt-neutral-gray-12">
                  {t("askV2.rail.overview")}
                </h3>
                <p className="text-[13px] leading-relaxed text-puntt-neutral-gray-11">
                  {content.context.overview}
                </p>
              </div>
            ) : null}
            {content.context.upcomingWork ? (
              <div data-testid="ask-v2-rail-upcoming">
                <h3 className="mb-1 text-[13px] font-bold text-puntt-neutral-gray-12">
                  {t("campaignDocsPanel.nextSteps")}
                </h3>
                <p className="text-[13px] leading-relaxed text-puntt-neutral-gray-11">
                  {content.context.upcomingWork}
                </p>
              </div>
            ) : null}
            {content.context.connectors.length > 0 ? (
              <div data-testid="ask-v2-rail-connectors">
                <h3 className="mb-2 text-[13px] font-bold text-puntt-neutral-gray-12">
                  {t("connectors")}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {content.context.connectors.map((connector) => (
                    <span
                      key={connector.id}
                      className="rounded-full bg-puntt-neutral-gray-3 px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-12"
                      data-testid={`ask-v2-rail-connector-${connector.id}`}
                    >
                      {connector.name}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {content.context.documents.length > 0 ? (
              <div data-testid="ask-v2-rail-documents">
                <h3 className="mb-2 text-[13px] font-bold text-puntt-neutral-gray-12">
                  {t("campaignDocsPanel.referenceDocuments")}
                </h3>
                <ul className="flex flex-col gap-2">
                  {content.context.documents.map((document) => (
                    <li
                      key={document.id}
                      className="flex items-center gap-2 text-[13px] text-puntt-neutral-gray-12"
                      data-testid={`ask-v2-rail-document-${document.id}`}
                    >
                      <FileText
                        size={16}
                        className="shrink-0 text-puntt-neutral-gray-10"
                        aria-hidden="true"
                      />
                      <span className="min-w-0 truncate">{document.name}</span>
                      {document.versionNumber != null ? (
                        <span className="shrink-0 text-[11px] text-puntt-neutral-gray-10">
                          v{document.versionNumber}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </AskV2RailSection>
      ) : null}
    </aside>
  );
}
